import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import { createTheme, ThemeProvider, useTheme, type Theme } from "@mui/material/styles";
import { DataGrid, type GridPaginationModel } from "@act-group/act-ui-library";
// Registers the MuiDataGrid key on the MUI theme's `components` map.
import "@mui/x-data-grid/themeAugmentation";
import { Verification } from "../../types/verification";
import { isHttpError, isValidationError } from "../../errors/isApiError";
import { siConsoleGridSx } from "../../theme/dataGridSx";
import { getVerificationColumns } from "./verificationColumns";
import VerificationsGridToolbar from "./VerificationsGridToolbar";

/**
 * Shows the toolbar, and swaps the v9 default (icon-only buttons plus a search
 * box) for the Admin Portal's layout — see VerificationsGridToolbar.
 *
 * Both go through the theme because the shared library's DataGrid forwards
 * neither `showToolbar` nor `slots`, and this is the configure-don't-replace
 * route: the alternative is dropping the shared wrapper and re-implementing its
 * striping/header styling here. `slots` is merged with the grid's defaults, so
 * naming `toolbar` replaces only that one. Nested around this grid rather than
 * set in actBrandTheme so the Users grid keeps its current chrome.
 */
const buildGridTheme = (jurisdiction?: string) => (outerTheme: Theme) =>
  createTheme(outerTheme, {
    components: {
      MuiDataGrid: {
        defaultProps: {
          showToolbar: true,
          slots: { toolbar: VerificationsGridToolbar },
          // The toolbar's own props travel the same route, since the wrapper
          // forwards no `slotProps` either.
          slotProps: { toolbar: { jurisdiction } },
        },
      },
    },
  });

export interface VerificationsGridProps {
  rows: Verification[];
  rowCount: number;
  isLoading: boolean;
  error: unknown;
  paginationModel: GridPaginationModel;
  onPaginationModelChange: (model: GridPaginationModel) => void;
  /** Jurisdiction the list is scoped to, if any — named in the export file. */
  jurisdiction?: string;
}

function VerificationsGrid({
  rows,
  rowCount,
  isLoading,
  error,
  paginationModel,
  onPaginationModelChange,
  jurisdiction,
}: VerificationsGridProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation();
  const columns = useMemo(() => getVerificationColumns(t), [t]);
  // Memoized so a new theme isn't built (and the whole grid re-rendered) on
  // every parent render — only when the jurisdiction actually changes.
  const gridTheme = useMemo(() => buildGridTheme(jurisdiction), [jurisdiction]);

  if (error) {
    return (
      <Alert severity="error">
        {isHttpError(error) || isValidationError(error)
          ? error.message
          : t("Failed to load verifications.")}
      </Alert>
    );
  }

  return (
    <ThemeProvider theme={gridTheme}>
      <Box sx={siConsoleGridSx(theme.palette.mode)}>
        <DataGrid<Verification>
          columns={columns}
          rows={rows}
          getRowId={(row) => row.verificationId}
          loading={isLoading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={onPaginationModelChange}
          rowCount={rowCount}
          onRowClick={(row) =>
            navigate(`/verifications/${row.verificationId}`, {
              // Includes the query string (not just pathname) so filters like
              // the Dashboard's jurisdiction/date-range survive the round trip
              // through the detail page and back.
              state: { from: location.pathname + location.search },
            })
          }
        />
      </Box>
    </ThemeProvider>
  );
}

export default VerificationsGrid;
